import React, { useState, useCallback, useMemo } from 'react';
import { TextField } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MORE_AVAILABLE_OPTION_ID } from '~/modules/common/components/SearchAutocomplete/SearchAutocomplete';
import CheckboxOption from '../CheckboxOption';
import { useTaskDropdownOptions } from './useTaskDropdownOptions';

export const useTaskDropdownHandlers = ({
  value,
  showClosedTasks,
  onToggleClosedTasks,
  onClose,
  projectId,
  onChange,
  label,
  classes,
  onInputChange
}) => {
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState('');

  const {
    loading,
    tasks,
    hasMore,
    isTimeEntryAllowedForProject
  } = useTaskDropdownOptions({
    projectId,
    searchTerm: value && value.id ? '' : searchTerm,
    showClosedTasks,
    skip: !projectId
  });

  const noTaskOption = useMemo(
    () => ({
      id: 'no-task',
      isNoTask: true,
      displayText: formatMessage({ id: 'moveTimeDialog.noTask' }),
      hierarchyLevel: 0,
      parents: [],
      fullPath: [],
      fullPathText: formatMessage({ id: 'moveTimeDialog.noTask' }),
      isParentPlaceholder: false
    }),
    [formatMessage]
  );

  const showClosedTasksOption = useMemo(
    () => ({
      id: 'show-closed-tasks',
      displayText: formatMessage({
        id: 'moveTimeDialog.showClosedTasks'
      }),
      isCheckbox: true
    }),
    [formatMessage]
  );

  const options = useMemo(() => {
    const baseOptions = [showClosedTasksOption];

    if (loading)
      return [
        ...baseOptions,
        {
          id: 'loading-tasks',
          displayText: formatMessage({
            id: 'moveTimeDialog.loading'
          }),
          isLoading: true,
          disabled: true
        }
      ];

    if (isTimeEntryAllowedForProject) baseOptions.push(noTaskOption);

    if (tasks.length === 0 && baseOptions.length === 1)
      return [
        ...baseOptions,
        {
          id: 'no-options',
          displayText: formatMessage({ id: 'moveTimeDialog.noMatchingTasks' }),
          isNoOptions: true,
          disabled: true
        }
      ];

    return [...baseOptions, ...tasks];
  }, [
    showClosedTasksOption,
    loading,
    isTimeEntryAllowedForProject,
    noTaskOption,
    tasks,
    formatMessage
  ]);

  const handleInputChange = useCallback(
    (event, newValue, reason) => {
      if (newValue === formatMessage({ id: 'moveTimeDialog.noTask' })) {
        onInputChange(event, newValue, reason);

        return;
      }
      setSearchTerm(newValue);
      onInputChange(event, newValue, reason);
    },
    [formatMessage, onInputChange]
  );

  const handleChange = useCallback(
    (selectedValue, event, reason) => {
      if (selectedValue && selectedValue.id === showClosedTasksOption.id) {
        onToggleClosedTasks();

        return;
      }

      if (selectedValue && selectedValue.isNoTask) {
        onChange(noTaskOption, event, reason);

        if (onClose) {
          onClose();
        }

        return;
      }

      onChange(selectedValue, event, reason);

      if (onClose) {
        onClose();
      }
    },
    [
      onChange,
      onClose,
      onToggleClosedTasks,
      noTaskOption,
      showClosedTasksOption.id
    ]
  );

  const filterOptions = useCallback(
    (allOptions, state) => {
      const checkboxOption = allOptions.find(
        opt => opt.id === showClosedTasksOption.id
      );
      const otherOptions = allOptions.filter(
        opt => opt.id !== showClosedTasksOption.id
      );

      const filtered = otherOptions.filter(option => {
        if (option.isLoading || !state.inputValue) return true;

        return option.displayText
          ?.toLowerCase()
          .includes(state.inputValue.toLowerCase());
      });

      if (state.inputValue && filtered.length === 0) {
        const noOptionsItem = {
          id: 'no-options',
          displayText: formatMessage({ id: 'moveTimeDialog.noMatchingTasks' }),
          isNoOptions: true,
          disabled: true
        };

        return checkboxOption
          ? [checkboxOption, noOptionsItem]
          : [noOptionsItem];
      }

      return checkboxOption ? [checkboxOption, ...filtered] : filtered;
    },
    [formatMessage, showClosedTasksOption.id]
  );

  const getOptionLabel = useCallback(
    option => {
      if (option.id === showClosedTasksOption.id) return '';

      return option.displayText || '';
    },
    [showClosedTasksOption.id]
  );

  const getOptionSelected = useCallback(
    (option, selected) => {
      if (option.id === showClosedTasksOption.id) return false;

      return option.id === selected.id;
    },
    [showClosedTasksOption.id]
  );

  const getOptionDisabled = useCallback(
    option =>
      option.id === MORE_AVAILABLE_OPTION_ID ||
      option.isParentPlaceholder ||
      option.isLoading ||
      option.isNoOptions ||
      (loading && option.id === showClosedTasksOption.id),
    [loading, showClosedTasksOption.id]
  );

  const renderInput = useCallback(
    params => <TextField {...params} label={label} />,
    [label]
  );

  const handleCheckboxKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onToggleClosedTasks();
      }
    },
    [onToggleClosedTasks]
  );

  const renderOption = useCallback(
    option => {
      if (option.id === showClosedTasksOption.id) {
        return (
          <CheckboxOption
            checked={showClosedTasks}
            label={formatMessage({
              id: 'moveTimeDialog.showClosedTasks'
            })}
            onChange={onToggleClosedTasks}
            onKeyDown={handleCheckboxKeyDown}
            option={option}
          />
        );
      }

      const hierarchyLevel = option.hierarchyLevel || 0;
      const paddingLeft = hierarchyLevel * 12;

      const optionClassName = [
        classes?.hierarchyOption,
        option.isParentPlaceholder && classes?.parentPlaceholder,
        option.isClosed && classes?.closedTask
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <div className={optionClassName} style={{ paddingLeft }}>
          {option.displayText}
        </div>
      );
    },
    [
      showClosedTasksOption.id,
      classes?.hierarchyOption,
      classes?.parentPlaceholder,
      classes?.closedTask,
      showClosedTasks,
      formatMessage,
      onToggleClosedTasks,
      handleCheckboxKeyDown
    ]
  );

  return {
    loading,
    hasMore,
    options,
    handleInputChange,
    handleChange,
    filterOptions,
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    renderInput,
    renderOption,
    showClosedTasksOptionId: showClosedTasksOption.id
  };
};

export default useTaskDropdownHandlers;
